import React, { useState } from 'react';
import { Btn, C } from '../../../components/UI';
import { api, MarketplaceAppDetailDto, TenantAppInstallationDto } from '../../../services/api';
import { useFeatureFlags, FeatureFlags } from '../../../context/FeatureFlagContext';
import AppSetupModal from './AppSetupModal';

// OAuthConnect apps point at a known provider key rather than a generic OAuth client this
// marketplace manages itself (that's real third-party-phase work). QuickBooks is the only entry
// today — it reuses the exact connect flow QuickBooksPanel already has.
const OAUTH_CONNECT_HANDLERS: Record<string, () => Promise<string>> = {
    quickbooks: async () => (await api.qboGetConnectUrl()).url,
    salesforce: async () => (await api.sfdcGetConnectUrl()).url,
};

// Marketplace app key -> the FeatureFlags key that actually gates it. The TenantAppInstallation
// record (what "Subscribe"/"Install" writes) is purely a billing/storefront record, decoupled
// from the feature flag that actually turns the functionality on — an admin can (and for
// Document Shredding, did) turn the flag on directly in Feature Flags without ever going through
// checkout. This mapping is what lets the marketplace page show "Installed" in that case too,
// instead of showing a stale "Subscribe" button for a feature that's already live.
const APP_FEATURE_FLAGS: Record<string, keyof FeatureFlags> = {
    'document-shredding': 'documentShredding',
};

export default function AppDetailView({ app, installation, onBack, onChanged }: {
    app: MarketplaceAppDetailDto;
    installation: TenantAppInstallationDto | undefined;
    onBack: () => void;
    onChanged: () => void;
}) {
    const [working, setWorking] = useState(false);
    const [error, setError] = useState('');
    const [showSetup, setShowSetup] = useState(false);
    const featureFlags = useFeatureFlags();

    const flagKey = APP_FEATURE_FLAGS[app.key];
    const featureEnabled = flagKey ? featureFlags[flagKey] : false;
    const isActive = installation?.status === 'Active' || featureEnabled;
    const isPendingSetup = installation?.status === 'PendingSetup';

    let connectConfig: { connectEndpointKey?: string } = {};
    try { connectConfig = app.setupConfigJson ? JSON.parse(app.setupConfigJson) : {}; } catch { /* ignore */ }

    const primaryAction = async () => {
        setError('');
        setWorking(true);
        try {
            if (app.pricingModel === 'Paid' && !isActive) {
                const returnUrl = window.location.href;
                const { checkoutUrl } = await api.checkoutApp(app.id, returnUrl, returnUrl);
                window.location.href = checkoutUrl;
                return;
            }
            if (app.setupType === 'OAuthConnect') {
                const handler = connectConfig.connectEndpointKey ? OAUTH_CONNECT_HANDLERS[connectConfig.connectEndpointKey] : undefined;
                if (!handler) throw new Error('This app\'s connection isn\'t configured yet.');
                window.location.href = await handler();
                return;
            }
            if (app.setupType === 'ApiKeyEntry') {
                setShowSetup(true);
                return;
            }
            // Toggle
            await api.installApp(app.id);
            onChanged();
        } catch (e: any) {
            setError(e.message || 'Something went wrong.');
        } finally {
            setWorking(false);
        }
    };

    const uninstall = async () => {
        if (!window.confirm(`Remove ${app.name} from your account?`)) return;
        setWorking(true);
        setError('');
        try { await api.uninstallApp(app.id); onChanged(); }
        catch (e: any) { setError(e.message || 'Something went wrong.'); }
        finally { setWorking(false); }
    };

    const actionLabel = () => {
        if (isActive) return 'Installed';
        if (app.pricingModel === 'Paid') return working ? 'Redirecting…' : `Subscribe — $${app.monthlyPrice}/mo`;
        if (app.setupType === 'OAuthConnect') return working ? 'Opening…' : 'Connect';
        if (app.setupType === 'ApiKeyEntry') return 'Configure';
        return working ? 'Installing…' : 'Install';
    };

    return (
        <div>
            <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.textSub, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 18 }}>
                ← App Marketplace
            </button>

            <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '2 1 480px', minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 20 }}>
                        <div style={{
                            width: 64, height: 64, borderRadius: 14, overflow: 'hidden', flexShrink: 0,
                            background: C.bg, border: `1px solid ${C.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {app.logoUrl
                                ? <img src={app.logoUrl} alt={app.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <span style={{ fontSize: 26, fontWeight: 700, color: C.textMuted }}>{app.name.charAt(0)}</span>}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {isActive && <span style={{ fontSize: 11, fontWeight: 700, color: C.green, background: C.greenBg, padding: '2px 9px', borderRadius: 20, marginBottom: 6, display: 'inline-block' }}>Installed</span>}
                            <h2 style={{ margin: '0 0 6px', fontSize: 21, fontWeight: 700, color: C.text }}>{app.name}</h2>
                            <p style={{ margin: '0 0 14px', fontSize: 14, color: C.textSub, lineHeight: 1.5 }}>{app.shortDescription}</p>
                            {isActive ? (
                                installation?.status === 'Active' ? (
                                    <Btn variant="outline" onClick={uninstall} disabled={working}>{working ? 'Removing…' : 'Remove app'}</Btn>
                                ) : (
                                    <div style={{ fontSize: 12, color: C.textSub }}>Enabled via the Feature Flags panel</div>
                                )
                            ) : (
                                <Btn onClick={primaryAction} disabled={working || isPendingSetup}>{isPendingSetup ? 'Setup pending…' : actionLabel()}</Btn>
                            )}
                        </div>
                    </div>

                    {error && <div style={{ color: '#D84040', fontSize: 13, marginBottom: 16 }}>{error}</div>}

                    {app.longDescriptionHtml && (
                        <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6, marginBottom: 28 }}
                            dangerouslySetInnerHTML={{ __html: app.longDescriptionHtml }} />
                    )}

                    {app.features.length > 0 && (
                        <Section title="Features & benefits">
                            {app.features.map((f, i) => (
                                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                                    <span style={{ color: C.green, fontWeight: 700, flexShrink: 0 }}>✓</span>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 13.5, color: C.text }}>{f.title}</div>
                                        {f.description && <div style={{ fontSize: 13, color: C.textSub }}>{f.description}</div>}
                                    </div>
                                </div>
                            ))}
                        </Section>
                    )}

                    {app.prerequisites.length > 0 && (
                        <Section title="Before you start">
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: C.textSub, lineHeight: 1.8 }}>
                                {app.prerequisites.map((p, i) => <li key={i}>{p}</li>)}
                            </ul>
                        </Section>
                    )}

                    {app.installSteps.length > 0 && (
                        <Section title="Installation steps">
                            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: C.textSub, lineHeight: 1.8 }}>
                                {app.installSteps.map((s, i) => <li key={i}>{s}</li>)}
                            </ol>
                        </Section>
                    )}

                    {app.screenshotUrls.length > 0 && (
                        <Section title="Gallery">
                            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                                {app.screenshotUrls.map((url, i) => (
                                    <img key={i} src={url} alt="" style={{ height: 140, borderRadius: 8, border: `1px solid ${C.border}` }} />
                                ))}
                            </div>
                        </Section>
                    )}
                </div>

                <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Developer</div>
                        <div style={{ fontSize: 13.5, color: C.text, marginBottom: 16 }}>{app.developerName}</div>

                        <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Need help?</div>
                        {app.supportEmail && <div style={{ fontSize: 13, marginBottom: 4 }}><a href={`mailto:${app.supportEmail}`} style={{ color: 'var(--color-button, #1D9E75)' }}>{app.supportEmail}</a></div>}
                        {app.supportUrl && <div style={{ fontSize: 13 }}><a href={app.supportUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-button, #1D9E75)' }}>Support site ↗</a></div>}
                        {!app.supportEmail && !app.supportUrl && <div style={{ fontSize: 13, color: C.textMuted }}>No support info provided.</div>}
                    </div>
                </div>
            </div>

            {showSetup && (
                <AppSetupModal
                    app={app}
                    onClose={() => setShowSetup(false)}
                    onDone={() => { setShowSetup(false); onChanged(); }}
                />
            )}
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.textSub, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</div>
            {children}
        </div>
    );
}
